import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useState } from 'react';
import * as styles from './SidebarRow.module.scss';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Caret from '../components/Caret';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import addTestId from '../utils/addTestId';

interface SidebarRowRightContentProps {
  menuItems?: React.ReactNode;
  customMenu?: React.ReactNode;
  expandState: 'collapsed' | 'expanded' | 'invisible' | 'none';
  onExpandClick?: React.MouseEventHandler;
}

export const SidebarRowRightContent = ({
  menuItems,
  customMenu,
  expandState,
  onExpandClick,
}: SidebarRowRightContentProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const stopEvent = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    stopEvent(e);
    setIsMenuOpen(!isMenuOpen);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    if (onExpandClick) {
      stopEvent(e);
      onExpandClick(e);
    }
  };

  return (
    <div className={styles.rightContent}>
      {!selectedSectionId && (!!menuItems || !!customMenu) && (
        <div
          className={classNames(styles.menuWrapper, {
            [styles.open]: isMenuOpen,
          })}
        >
          <div className={styles.menuContent}>
            {customMenu}
            {!!menuItems && (
              <div
                onClick={stopEvent /* Prevent collapsing/expanding row item */}
              >
                <OverflowMenu
                  button={
                    <div
                      className={styles.menuButton}
                      onClick={handleMenuClick}
                      {...addTestId('sidebarRowMenuButton')}
                    >
                      <SVGIcon size={16}>{overflowMenuIcon}</SVGIcon>
                    </div>
                  }
                  onOpenChange={setIsMenuOpen}
                  open={isMenuOpen}
                  placement="bottom-start"
                >
                  {menuItems}
                </OverflowMenu>
              </div>
            )}
          </div>
        </div>
      )}
      {expandState !== 'none' && (
        <div
          className={classNames(styles.caretContainer, {
            [styles.hidden]: expandState === 'invisible',
          })}
          onClick={handleExpandClick}
        >
          <Caret
            direction={expandState === 'expanded' ? 'up' : 'down'}
            size={10}
          />
        </div>
      )}
    </div>
  );
};

interface SidebarRowProps {
  icon?: React.ReactNode;
  rightContent?: React.ReactNode;
  subItem?: boolean;
  children: React.ReactNode;
  type?: 'post' | 'topic' | 'template';
  level?: number;
  to?: string;
  onClick?: React.MouseEventHandler;
  onlyActiveOnIndex?: boolean;
  active?: boolean;
}

const SidebarRow = ({
  icon,
  rightContent,
  subItem,
  children,
  type,
  level,
  to,
  onClick,
  onlyActiveOnIndex,
  active,
}: SidebarRowProps) => {
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const content = (
    <>
      {!!icon && (
        <div className={styles.icon}>
          {typeof icon === 'string' ? (
            <SVGIcon size={subItem ? 12 : 'regular'}>{icon}</SVGIcon>
          ) : (
            icon
          )}
        </div>
      )}
      <div className={styles.content}>{children}</div>
      {!!rightContent && (
        <div className={styles.rightContent}>{rightContent}</div>
      )}
    </>
  );

  const isClickable = !!to || !!onClick;
  const className = classNames(styles.container, {
    [styles.subItem]: subItem,
    [styles.active]: active,
    [styles.post]: type === 'post',
    [styles.topic]: type === 'topic',
    [styles.template]: type === 'template',
    [styles.clickable]: isClickable,
  });

  return (
    <div className={styles.rowContainer}>
      {!!level &&
        _.times(level, (index) => (
          <div
            key={index}
            className={classNames(styles.indentLine, {
              [styles.first]: index === 0,
              [styles.last]: index === level - 1,
            })}
          />
        ))}
      {isClickable ? (
        <Button
          to={to}
          onClick={(event) => {
            if (selectedSectionId) {
              event.preventDefault();
            }
            onClick?.(event);
          }}
          activeClassName={styles.active}
          onlyActiveOnIndex={onlyActiveOnIndex}
          className={className}
        >
          {content}
        </Button>
      ) : (
        <div className={className}>{content}</div>
      )}
    </div>
  );
};

export default SidebarRow;
